/**
 * Caregiver ClientProofsPage (CONTRACTS §6, spec §9.3 / §9.5): proof list
 * for a linked client, signed playback via getProofSignedUrl (no public
 * URLs), and 'Markeer als bekeken' which records the viewed timestamp.
 * Includes retention/deletion copy per the privacy gate (§9.6).
 */
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Banner,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Skeleton,
  StatusChip,
  useToast,
} from '@/components/ui'
import { CheckIcon, ChevronLeftIcon, ShieldIcon, VideoIcon } from '@/components/icons'
import { repositories } from '@/services/repositories'
import { PATHS } from '@/app/router/paths'
import type { MedicationIntakeEvent } from '@/types'
import {
  ProofPlayer,
  errorMessage,
  formatDutchDateTime,
  useIntakeHistory,
} from '@/features/medication'
import './caregiver.css'

export default function ClientProofsPage() {
  const { clientId } = useParams<{ clientId: string }>()
  const navigate = useNavigate()
  const { showToast } = useToast()
  const { status, events, error, refresh } = useIntakeHistory(clientId ?? null, 100)

  const [openProofId, setOpenProofId] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const proofs = events.filter((event) => event.proofObjectPath !== null)

  const markViewed = async (event: MedicationIntakeEvent) => {
    if (busyId || event.caregiverViewedAt) return
    setBusyId(event.id)
    try {
      await repositories.medication.markProofViewed(event.id)
      showToast({ message: 'Gemarkeerd als bekeken. Dit tijdstip is vastgelegd.', tone: 'success' })
      await refresh()
    } catch (err) {
      showToast({ message: errorMessage(err), tone: 'error' })
    } finally {
      setBusyId(null)
    }
  }

  if (!clientId) {
    return (
      <EmptyState
        icon={<VideoIcon size={26} />}
        title="Cliënt niet gevonden"
        action={
          <Button variant="secondary" onClick={() => navigate(PATHS.caregiver.clients)}>
            Terug naar cliënten
          </Button>
        }
      />
    )
  }

  return (
    <div className="caregiver-page">
      <button
        type="button"
        className="caregiver-back-link"
        onClick={() => navigate(PATHS.caregiver.clientDetail(clientId))}
      >
        <ChevronLeftIcon size={16} />
        Terug naar cliënt
      </button>

      <header>
        <h1 className="caregiver-section-title">Bewijsvideo’s</h1>
        <p className="caregiver-muted">
          Video’s die de cliënt zelf heeft ingestuurd als bewijs van een inname.
        </p>
      </header>

      <Banner tone="info">
        Een bewijsvideo is door de cliënt ingestuurd bewijs — het is geen medische verificatie. Er
        wordt nooit automatisch beoordeeld of medicatie correct is ingenomen.
      </Banner>

      {status === 'loading' ? <Skeleton lines={3} /> : null}
      {status === 'unavailable' ? (
        <Card padding="md">
          <p className="caregiver-muted">Cloud niet ingesteld. Bewijsvideo’s kunnen nu niet worden opgehaald.</p>
        </Card>
      ) : null}
      {status === 'error' ? <ErrorState error={error ?? undefined} onRetry={() => void refresh()} /> : null}

      {status === 'ready' ? (
        proofs.length === 0 ? (
          <EmptyState
            icon={<VideoIcon size={26} />}
            title="Nog geen bewijsvideo’s"
            body="Wanneer de cliënt een bewijsvideo uploadt bij een inname, verschijnt die hier."
          />
        ) : (
          <Card padding="sm">
            <ul className="caregiver-list" aria-label="Bewijsvideo’s">
              {proofs.map((event) => {
                const viewed = event.caregiverViewedAt !== null
                return (
                  <li key={event.id} className="caregiver-link-item">
                    <div className="caregiver-link-header">
                      <span className="caregiver-event-text">
                        <span className="caregiver-event-title">
                          Geüpload op {formatDutchDateTime(event.proofUploadedAt ?? event.updatedAt)}
                        </span>
                        {event.takenAt ? (
                          <span className="caregiver-event-meta">
                            Inname op {formatDutchDateTime(event.takenAt)}
                          </span>
                        ) : null}
                        {viewed ? (
                          <span className="caregiver-event-meta">
                            Bekeken op {formatDutchDateTime(event.caregiverViewedAt ?? '')}
                          </span>
                        ) : null}
                      </span>
                      <StatusChip status={viewed ? 'success' : 'info'}>
                        {viewed ? 'Bekeken' : 'Nieuw bewijs'}
                      </StatusChip>
                    </div>

                    {openProofId === event.id ? (
                      <ProofPlayer intakeEvent={event} />
                    ) : (
                      <div className="caregiver-actions">
                        <Button variant="secondary" size="sm" onClick={() => setOpenProofId(event.id)}>
                          <VideoIcon size={16} />
                          Video openen
                        </Button>
                      </div>
                    )}

                    {!viewed ? (
                      <div className="caregiver-actions">
                        <Button
                          variant="primary"
                          size="sm"
                          loading={busyId === event.id}
                          onClick={() => void markViewed(event)}
                        >
                          <CheckIcon size={16} />
                          Markeer als bekeken
                        </Button>
                      </div>
                    ) : null}
                  </li>
                )
              })}
            </ul>
          </Card>
        )
      ) : null}

      <Card padding="md">
        <div className="caregiver-section">
          <h2 className="caregiver-section-title">
            <ShieldIcon size={18} /> Bewaren en verwijderen
          </h2>
          <p className="caregiver-muted">
            Bewijsvideo’s worden privé opgeslagen en zijn alleen bereikbaar via kortdurende,
            ondertekende links — er bestaat geen openbare URL. Video’s worden bewaard volgens de
            afgesproken bewaartermijn en daarna automatisch verwijderd. De cliënt kan altijd om
            verwijdering vragen; verwijderen wist ook het opgeslagen bestand. Elke weergave en
            verwijdering wordt vastgelegd in het auditlog.
          </p>
        </div>
      </Card>
    </div>
  )
}
